/* Copyright 2013 - 2024 Waiterio LLC */
/** @jsx jsx */
import React from 'react'
import { css, jsx } from '@emotion/react'
import { Navigate, useParams } from 'react-router-dom'
import { useTranslation } from '@multilocale/react/index.js'
import getArticles from '@polyblog/client/getArticles.js'
import useArticles from '@polyblog/react/useArticles.js'
import IconFacebook from '@stiloso/icons/IconFacebook.js'
import IconLinkedin from '@stiloso/icons/IconLinkedin.js'
import IconTwitter from '@stiloso/icons/IconTwitter.js'
import IconPapillon from '@stiloso/icons/IconPapillon.js'
import IconDone from '@stiloso/icons/IconDone.js'
import colMd4 from '@waiterio/styles/bootstrap/colMd4.js'
import colMd7 from '@waiterio/styles/bootstrap/colMd7.js'
import colMd10 from '@waiterio/styles/bootstrap/colMd10.js'
import colMd12 from '@waiterio/styles/bootstrap/colMd12.js'
import colLg2 from '@waiterio/styles/bootstrap/colLg2.js'
import dLgBlock from '@waiterio/styles/bootstrap/dLgBlock.js'
import dNone from '@waiterio/styles/bootstrap/dNone.js'
import colLg8 from '@waiterio/styles/bootstrap/colLg8.js'
import colSm12 from '@waiterio/styles/bootstrap/colSm12.js'
import container from '@waiterio/styles/bootstrap/container.js'
import row from '@waiterio/styles/bootstrap/row.js'
import Layout from '../components/LayoutComponent.js'
import formatDate from '../formatDate.js'
import authors from '../authors.json'
import languages from '../languages.js'
import masthead from '../styles/masthead.js'
import overlay from '../styles/overlay.js'
import { blogId } from '../polyblog.config.js'

const postHeading = css`
  color: white;
  margin-bottom: -130px;
  padding: 200px 0 150px;
  @media (min-width: 768px) {
    padding: 200px 0;
  }
`

const articleTitle = css`
  font-size: 35px;
  font-weight: 800;
  line-height: 58px;
  margin-bottom: 0px;
  margin-top: 0px;
  @media (min-width: 768px) {
    font-size: 55px;
  }
`

const articleDescription = css`
  font-size: 24px;
  font-weight: 600;
  line-height: 1.1;
  margin-bottom: 30px;
  margin-top: 10px;
  @media (min-width: 768px) {
    font-size: 30px;
  }
`

const articlePostedBy = css`
  font-family: Georgia, Cambria, 'Times New Roman', Times, serif;
  font-size: 20px;
  font-style: italic;
  font-weight: 300;
  line-height: 22px;
`

const sectionArticle = css`
  color: rgb(33, 37, 41);
  display: block;
  font-size: 20px;
  line-height: 26px;
  margin-bottom: 15px;
  margin-top: 15px;
`

const containerFluid = css`
  width: 100%;
  padding-right: 15px;
  padding-left: 15px;
  margin-right: auto;
  margin-left: auto;
`

const articleBox = css`
  padding: 20px 15px 15px 15px;
`

const articleContent = css`
  color: rgb(33, 37, 41);
  font-family: Georgia, Cambria, 'Times New Roman', Times, serif;
  font-size: 20px;
  line-height: 35px;
  & a {
    color: var(--color-primary);
    :hover {
      color: #5cb85c;
    }
  }
  & img {
    padding-bottom: 24px;
    display: block;
    margin-left: auto;
    margin-right: auto;
    max-width: 512px;
  }
  & p {
    margin: 30px 0;
  }
  & h2 {
    font-size: 35px;
    font-weight: 800;
    line-height: 46px;
    margin-left: 0px;
    margin-right: 0px;
    margin-top: 1.5em;
    margin-bottom: 1.5em;
    @media (max-width: 767px) {
      font-size: 34px;
    }
  }
  & h3 {
    font-size: 30px;
    font-weight: 500;
    line-height: 40px;
    margin-left: 0px;
    margin-right: 0px;
    margin-top: 1.5em;
    margin-bottom: 1.5em;
    @media (max-width: 767px) {
      font-size: 26px;
      line-height: 32px;
    }
  }

  & ul {
    margin: 0px;
    padding: 5px;
  }

  & ol > ul {
    margin-left: 20px;
  }

  & li {
    margin: 15px;
  }

  .iframe-video-container {
    display: flex;
    flex: 1;
    justify-content: center;
  }

  & table {
    border: 1px solid #666;
    border-collapse: collapse;
  }

  & th {
    text-align: left;
    border: 1px solid #666;
    padding: 4px 16px;
    height: 34px;
    line-height: 34px;
  }

  & td {
    text-align: left;
    border: 1px solid #666;
    height: 34px;
    padding: 4px 16px;
    line-height: 34px;
  }
`

const widgetTitle = css`
  color: rgb(51, 51, 51);
  font-size: 24px;
  font-weight: 600;
  line-height: 24px;
  margin-block-end: 20px;
  margin-block-start: 0px;
  margin-bottom: 20px;
  padding-bottom: 10px;
  text-transform: capitalize;
`

const recentArticleTitle = css`
  color: #666;
  cursor: pointer;
  font-family: Georgia, Cambria, 'Times New Roman', Times, serif;
  font-size: 16px;
  font-weight: 600;
`

const recentArticleDescription = css`
  font-family: Georgia, Cambria, 'Times New Roman', Times, serif;
  font-size: 16px;
  margin-top: 15px;
`

const recentArticlePostedBy = css`
  color: #666;
  font-family: Georgia, Cambria, 'Times New Roman', Times, serif;
  font-size: 15px;
  padding-inline-start: 2px;
  margin-top: 15px;
  overflow: hidden;
  white-space: nowrap;
`

const sectionDownload = css`
  background-color: #3f92d2;
  padding: 60px 0px;
`

const downloadButton = css`
  background-color: white;
  border-radius: 4px;
  color: rgb(51, 51, 51);
  font-size: 16px;
  font-weight: 500;
  padding: 16px 32px;
  margin-top: 16px;
  width: 180px;
  text-align: center;
  :hover {
    color: var(--color-primary);
  }

  @media (max-width: 767px) {
    margin-left: auto;
    margin-right: auto;
  }
`

const translatedArticleTick = css`
  border: 1px solid white;
  padding: 2px;
  border-radius: 16px;
  fill: white;
  vertical-align: sub;
  margin-left: 8px;
`

const errorStyle = css({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: 160,
  fontSize: 36,
  color: 'red',
})

const tabletFrame = css({
  margin: 'auto 16px',
  width: 579,
  height: 386,
  borderRadius: 10,
  border: '10px solid #535150',
  background: 'white',
  '@media(max-width: 992px)': {
    width: 386,
    height: 257,
  },
  '@media(max-width: 767px)': {
    margin: '32px 16px',
  },
  '@media(max-width: 579px)': {
    width: 308,
    height: 205,
  },
})

const RecentArticlesSection = ({ locale }) => {
  const { t } = useTranslation(locale)
  let { data: articles } = useArticles({
    blogId,
    locale,
    limit: 3,
    published: true,
    sortField: 'lastRewriteTime',
    sortDirection: 'DESC',
  })

  if (articles) {
    articles = Object.values(articles).map(article => {
      let postedBy = t('Posted by {{author}} on {{date}}')
      postedBy = postedBy
        .replace('{{author}}', article.author || 'Joe Dodds')
        .replace(
          '{{date}}',
          formatDate({ date: article.creationTime, locale: article.locale }),
        )

      return {
        ...article,
        url: `/blog/${article.locale}/${article.slug}/`,
        postedBy,
      }
    })
  }

  return (
    <section
      css={[
        {
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          backgroundColor: '#f5f5f5',
          paddingTop: 40,
          paddingBottom: 40,
          '@media(max-width:767px)': {
            paddingTop: 40,
            paddingBottom: 40,
          },
        },
      ]}
    >
      <div
        css={[
          row,
          {
            marginLeft: 40,
            marginRight: 40,
            '@media(max-width:767px)': {
              margin: 0,
            },
          },
        ]}
      >
        <h4 css={[widgetTitle, colMd12]}>{t('Recent articles')}</h4>
      </div>
      <div
        css={[
          row,
          {
            marginLeft: 40,
            marginRight: 40,
            marginBottom: 20,
            '@media(max-width:767px)': {
              margin: 0,
            },
          },
        ]}
      >
        {articles?.map(
          ({ _id, postedBy, coverUrl, title, url, description }) => (
            <div
              key={_id}
              css={[
                colSm12,
                colMd4,
                {
                  '@media(max-width:767px)': {
                    marginBottom: 15,
                    ':last-child': {
                      marginBottom: 0,
                    },
                  },
                },
              ]}
            >
              <div css={{ boxShadow: '0px 0px 16px 0px #C0C0C0' }}>
                <a href={url}>
                  <picture>
                    <source
                      type="image/webp"
                      media="(-webkit-min-device-pixel-ratio: 2)"
                      srcSet={coverUrl?.replace('.jpg', '-768w.webp')}
                    />
                    <source
                      type="image/webp"
                      srcSet={coverUrl?.replace('.jpg', '-640w.webp')}
                    />
                    <source
                      type="image/jpeg"
                      media="(-webkit-min-device-pixel-ratio: 2)"
                      srcSet={coverUrl?.replace('.jpg', '-768w.jpg')}
                    />
                    <source
                      type="image/jpeg"
                      srcSet={coverUrl?.replace('.jpg', '-640w.jpg')}
                    />
                    <img
                      src={coverUrl?.replace('.jpg', '-640w.jpg')}
                      alt={title}
                    />
                  </picture>
                  <div
                    css={{
                      backgroundColor: 'white',
                      padding: 14,
                      marginTop: -2,
                    }}
                  >
                    <div css={recentArticleTitle}>{title}</div>
                    <div css={recentArticleDescription}>{description}</div>
                    <div css={recentArticlePostedBy}>{postedBy}</div>
                  </div>
                </a>
              </div>
            </div>
          ),
        )}
      </div>
    </section>
  )
}

export const paths = languages.map(locale => ({
  path: `/blog/${locale}/:slug/`,
  props: {
    locale,
  },
}))

export const getStaticPaths = async () => {
  let paths = []
  let articles = await getArticles({
    blogId,
    fields: ['_id', 'locale', 'slug', 'slugsDeprecated'],
  })
  // console.log({ articles })

  articles.forEach(({ locale, slug /* , slugsDeprecated */ }) => {
    paths.push(`/blog/${locale}/${slug}`)

    // slugsDeprecated?.forEach(slugDeprecated => {
    //   paths.push(`/blog/${locale}/${slugDeprecated}/`)
    // })
  })

  return paths.sort()
}

export const Head = ({ locale }) => {
  const { slug } = useParams()
  const { t } = useTranslation(locale)
  const url = `/blog/${locale}/${slug}/`
  const canonical = `https://www.waiterio.com${url}`

  let { data: articles } = useArticles({
    blogId,
    slug,
    locale,
  })

  let article = articles.find(
    article => article.slug === slug || article.slugsDeprecated.includes(slug),
  )

  const title = article?.title
  const description = article?.description

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content="max-image-preview:large" />
      <meta name="twitter:title" content={article?.title} />
      <meta name="twitter:description" content={article?.description} />
      <meta name="twitter:image" content={article?.cover} />
      <meta property="og:site_name" content={t('Successful Restaurant')} />
      <meta property="og:title" content={article?.title} />
      <meta property="og:description" content={article?.description} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={article?.cover} />
      <meta itemProp="name" content={title} />
      <meta itemProp="description" content={article?.description} />
      {url && <link rel="canonical" href={canonical} />}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org/',
            '@type': 'Article',
            headline: article?.title,
            image: article?.cover,
            inLanguage: locale,
            author: {
              '@name': article?.author,
            },
            publisher: {
              '@id': 'https://www.waiterio.com/#corporation',
            },
            datePublished: new Date(
              article?.creationTime ?? null,
            )?.toISOString(),
            dateModified: new Date(
              article?.lastEditTime ?? null,
            )?.toISOString(),
          }),
        }}
      />
    </>
  )
}

const ArticlePage = ({ locale }) => {
  const { slug } = useParams()
  const { t } = useTranslation(locale)

  const url = `/blog/${locale}/${slug}/`
  let author

  let {
    data: articles,
    error,
    loading,
  } = useArticles({
    blogId,
    slug,
    locale,
  })

  // console.log({ articles })

  let article = articles?.find(
    article => article.slug === slug || article.slugsDeprecated.includes(slug),
  )

  if (article) {
    if (article?.locale === 'en') {
      author = authors.find(author => author.fullName === article.author)
    }

    article.postedBy = t('Posted by {{author}} on {{date}}')
      .replace('{{author}}', article.author || 'Joe Dodds')
      .replace(
        '{{date}}',
        formatDate({
          date: article.lastRewriteTime || article.creationTime,
          locale: article.locale,
        }),
      )

    article.title = article.title || `${article.title}`
    article.description = article.description || article.description
  }

  const isTranslated = !!article?.translatorId || !!article?.translator

  const onShareBlog = (href, shareType) => {
    window.open(href, shareType, 'width=780,height=500')
  }

  return (
    <Layout locale={locale}>
      {loading && <div className="loader" />}

      {error && <div css={errorStyle}>{error}</div>}
      {articles?.length === 0 && <Navigate to={`/blog/${locale}/not-found/`} />}
      {article && (
        <>
          <header css={masthead}>
            <picture>
              <source
                type="image/webp"
                media="(min-width: 1920px), (-webkit-min-device-pixel-ratio: 2) and (min-width: 960px)"
                srcSet={article.coverUrl?.replace('.jpg', '-1920w.webp')}
              />
              <source
                type="image/webp"
                media="(min-width: 1600px), (-webkit-min-device-pixel-ratio: 2) and (min-width: 800px)"
                srcSet={article.coverUrl?.replace('.jpg', '-1600w.webp')}
              />
              <source
                type="image/webp"
                media="(min-width: 1366px), (-webkit-min-device-pixel-ratio: 2) and (min-width: 683px)"
                srcSet={article.coverUrl?.replace('.jpg', '-1366w.webp')}
              />
              <source
                type="image/webp"
                media="(min-width: 1024px), (-webkit-min-device-pixel-ratio: 2) and (min-width: 512px)"
                srcSet={article.coverUrl?.replace('.jpg', '-1024w.webp')}
              />
              <source
                type="image/webp"
                media="(min-width: 768px), (-webkit-min-device-pixel-ratio: 2) and (min-width: 384px)"
                srcSet={article.coverUrl?.replace('.jpg', '-768w.webp')}
              />
              <source
                type="image/webp"
                srcSet={article.coverUrl?.replace('.jpg', '-640w.webp')}
              />

              <source
                type="image/jpeg"
                media="(min-width: 1920px), (-webkit-min-device-pixel-ratio: 2) and (min-width: 960px)"
                srcSet={article.coverUrl?.replace('.jpg', '-1920w.jpg')}
              />
              <source
                type="image/jpeg"
                media="(min-width: 1600px), (-webkit-min-device-pixel-ratio: 2) and (min-width: 800px)"
                srcSet={article.coverUrl?.replace('.jpg', '-1600w.jpg')}
              />
              <source
                type="image/jpeg"
                media="(min-width: 1366px), (-webkit-min-device-pixel-ratio: 2) and (min-width: 683px)"
                srcSet={article.coverUrl?.replace('.jpg', '-1366w.jpg')}
              />
              <source
                type="image/jpeg"
                media="(min-width: 1024px), (-webkit-min-device-pixel-ratio: 2) and (min-width: 512px)"
                srcSet={article.coverUrl?.replace('.jpg', '-1024w.jpg')}
              />
              <source
                type="image/jpeg"
                media="(min-width: 768px), (-webkit-min-device-pixel-ratio: 2) and (min-width: 384px)"
                srcSet={article.coverUrl?.replace('.jpg', '-768w.jpg')}
              />
              <source
                type="image/jpeg"
                srcSet={article.coverUrl?.replace('.jpg', '-640w.jpg')}
              />

              <img
                css={{
                  height: '100%',
                  position: 'absolute',
                  top: 0,
                  padding: 0,
                  objectFit: 'cover',
                }}
                src={article.coverUrl?.replace('.jpg', '-1024w.jpg')}
                alt={article.title}
              />
            </picture>
            <div css={overlay} className="overlay" />
            <div css={container}>
              <div css={row}>
                <div
                  css={[
                    colMd10,
                    colLg8,
                    { marginLeft: 'auto', marginRight: 'auto' },
                  ]}
                >
                  <div css={postHeading}>
                    <h1 css={articleTitle}>{article.title}</h1>
                    <h2 css={articleDescription}>{article.description}</h2>
                    <span css={articlePostedBy}>{article.postedBy}</span>
                    {isTranslated && <IconDone css={translatedArticleTick} />}
                  </div>
                </div>
              </div>
            </div>
          </header>
          <section css={[sectionArticle, containerFluid]}>
            <div css={{ display: 'flex', marginBottom: 15 }}>
              <div css={[colLg2, dNone, dLgBlock]} />
              <div
                css={[
                  colSm12,
                  colMd7,
                  colLg8,
                  { marginLeft: 'auto', marginRight: 'auto' },
                ]}
              >
                <div css={articleBox}>
                  <article
                    css={articleContent}
                    dangerouslySetInnerHTML={{ __html: article.content }}
                  />
                  <div
                    css={{
                      width: '100%',
                      marginTop: 30,
                      height: 2,
                      backgroundColor: '#f5f5f5',
                    }}
                  />
                  {author && (
                    <>
                      <div css={{ marginTop: 30, display: 'flex' }}>
                        <picture>
                          <source
                            type="image/webp"
                            srcSet={`${author.avatarUrl}.webp`}
                          />
                          <img
                            src={`${author.avatarUrl}.jpg`}
                            css={{ width: 60, height: 60, borderRadius: 36 }}
                          />
                        </picture>
                        <a
                          href={`/blog/${locale}/authors/${author.id}`}
                          css={{
                            marginTop: 'auto',
                            marginBottom: 'auto',
                            marginLeft: 15,
                            color: 'var(--color-primary)',
                          }}
                        >
                          {author.fullName}
                        </a>
                      </div>
                      <p css={{ fontSize: 18 }}>{author.shortDescription}</p>
                      <div
                        css={{
                          marginTop: 30,
                          width: '100%',
                          height: 2,
                          backgroundColor: '#f5f5f5',
                        }}
                      />
                    </>
                  )}
                </div>
              </div>
              <div css={[colLg2, dNone, dLgBlock]}>
                <div
                  css={{
                    float: 'right',
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'column',
                    justifyContent: 'center',
                    color: 'white',
                    textAlign: 'center',
                    padding: 12,
                    marginTop: 54,
                    marginBottom: 54,
                    position: 'sticky',
                    top: 54,
                    borderRadius: 4,
                    backgroundColor: '#5aa1d8',
                  }}
                >
                  <span css={{ fontSize: 18 }}>
                    {t('Grow your food business with Waiterio POS')}
                  </span>
                  <div css={{ display: 'flex', justifyContent: 'center' }}>
                    <IconPapillon
                      width="54"
                      height="70"
                      css={{ fill: 'white' }}
                    />
                  </div>
                  <a
                    href={`http://www.waiterio.com/${locale}`}
                    css={{ marginBottom: 5 }}
                  >
                    <button
                      type="submit"
                      css={{
                        cursor: 'pointer',
                        paddingTop: 12,
                        paddingBottom: 12,
                        width: '100%',
                        borderRadius: 4,
                        fontSize: 16,
                        fontWeight: '500',
                        backgroundColor: 'white',
                        border: 'white',
                        outline: 'none',
                      }}
                    >
                      {t('Get it for free')}
                    </button>
                  </a>
                </div>
              </div>
            </div>
            <div css={[row, { marginTop: 30, justifyContent: 'center' }]}>
              <div css={{ display: 'flex', justifyContent: 'center' }}>
                <div
                  css={{ height: 36 }}
                  onClick={() =>
                    onShareBlog(
                      `http://www.facebook.com/sharer/sharer.php?u=https://www.waiterio.com${url}`,
                      'Facebook',
                    )
                  }
                >
                  <IconFacebook
                    css={{
                      marginInlineStart: 5,
                      fill: '#666666',
                      width: 36,
                      height: 36,
                      cursor: 'pointer',
                      ':hover': { fill: '#3a5ca9' },
                    }}
                  />
                </div>
                <div
                  css={{ height: 36 }}
                  onClick={() =>
                    onShareBlog(
                      `https://www.linkedin.com/shareArticle?mini=true&url=https://www.waiterio.com${url}`,
                      'LinkedIn',
                    )
                  }
                >
                  <IconLinkedin
                    css={{
                      marginInlineStart: 5,
                      fill: '#666666',
                      width: 36,
                      height: 36,
                      cursor: 'pointer',
                      ':hover': { fill: '#006cac' },
                    }}
                  />
                </div>
                <div
                  css={{ height: 36 }}
                  onClick={() =>
                    onShareBlog(
                      `https://twitter.com/intent/tweet?text=https://www.waiterio.com${url}`,
                      'Twitter',
                    )
                  }
                  className="twitter-share-button"
                >
                  <IconTwitter
                    css={{
                      marginInlineStart: 9,
                      marginTop: 2,
                      fill: '#666666',
                      width: 28,
                      height: 28,
                      cursor: 'pointer',
                      ':hover': { fill: '#1a97f0' },
                    }}
                  />
                </div>
              </div>
            </div>
          </section>
          <section css={sectionDownload}>
            <div
              css={{
                display: 'flex',
                flex: 1,
                justifyContent: 'space-around',
                '@media(max-width: 767px)': {
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                },
              }}
            >
              <div
                css={{
                  display: 'flex',
                  flexDirection: 'column',
                  color: 'white',
                  margin: 'auto 16px',
                }}
              >
                <h3
                  css={{
                    fontSize: 32,
                    maxWidth: 450,
                    textTransform: 'capitalize',
                  }}
                >
                  {t(
                    'Join 40,000+ restaurants using waiterio point of sale system',
                    locale,
                  )}
                </h3>
                <p css={{ maxWidth: 500, lineHeight: '24px' }}>
                  <q>
                    The online ordering has been the perfect tool, especially
                    with the ongoing COVID-19 pandemic as customers choose to
                    limit face to face interaction. We have grown food delivery
                    by over 112% which is solely due to the use of the free
                    online ordering website.
                  </q>
                </p>
                <p css={{ lineHeight: '24px' }}>
                  - Matthew Johnson (Owner of <q>MrBreakFastJa</q>, Jamaica)
                </p>
                <a css={downloadButton} href={`/${locale}/downloads/`}>
                  {t('Free download')}
                </a>
              </div>
              <div css={tabletFrame}>
                <img
                  src={`https://www.imagelato.com/images/screenshot-2e0628e9-orders-tablet-${locale}.jpg`}
                  css={{ width: '100%', height: '100%' }}
                />
              </div>
            </div>
          </section>
          <RecentArticlesSection locale={locale} />
        </>
      )}
    </Layout>
  )
}

export default ArticlePage
